import React, { useState } from 'react';
import { RsvpForm } from '@/src/modules/rsvp';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';

const META: TemplateMetadata = {
  id: 'tpl_baby_shower_moon_stars_004',
  name: 'Moon & Stars',
  occasionType: 'BABY_SHOWER',
  description: 'A dreamy champagne gold and warm white invitation with stars, soft clouds, and a cute sleeping teddy on the moon.',
  isPremium: true,
  tags: ['baby-shower', 'premium', 'dreamy', 'moon', 'stars'],
  colorScheme: { primary: '#d97706', accent: '#f59e0b', bg: '#fef3c7' },
};

export function MoonStars({ data }: { data: InvitationData }) {
  const baby = data.customData?.baby ?? {};
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const [showRsvpModal, setShowRsvpModal] = useState(false);

  // Read dynamic variables with default fallbacks
  const motherName = baby.motherName || 'Emma Harrington';
  const hostedBy = baby.hostedBy || data.hostName || 'The Harrington Family';
  
  // Format Date and Day
  const rawDate = baby.eventDate || data.eventDate || new Date().toISOString();
  let formattedDate = 'December 20, 2026';
  let dayOfWeek = 'Sunday';
  try {
    const d = new Date(rawDate);
    formattedDate = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' });
  } catch (e) {}

  const eventTime = baby.eventTime || data.eventTime || '04:00 PM';
  const venueName = baby.venueName || data.eventVenue?.split(',')[0] || 'The Stardust Observatory';
  const venueAddress = baby.venueAddress || data.eventVenue || '555 Celestial Way, San Francisco, CA';
  
  const rsvpName = baby.rsvpName || 'Sophia';
  const rsvpPhone = baby.rsvpPhone || '+1 (555) 012-3456';
  const message = baby.message || data.customMessage || 'Love you to the moon and back. Join us to celebrate the upcoming arrival of our little star!';
  const dressCode = baby.dressCode || '';

  return (
    <div className="min-h-screen bg-[#faf8f4] py-12 px-4 flex flex-col items-center justify-start font-sans relative overflow-hidden">
      {/* Import Google Fonts */}
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400..900;1,400..900&family=Allura&family=Inter:wght@300;400;500;600&display=swap');
        .font-serif-dreamy { font-family: 'Playfair Display', Georgia, serif; }
        .font-cursive-dreamy { font-family: 'Allura', cursive; }
        .font-sans-dreamy { font-family: 'Inter', sans-serif; }
        .gold-glow {
          box-shadow: 0 0 25px rgba(217, 119, 6, 0.15);
        }
        .text-champagne {
          color: #c5a059;
        }
        .border-champagne {
          border-color: #d4af37;
        }
      `}} />

      {/* Main Card */}
      <div className="w-full max-w-[480px] aspect-[9/16] bg-[#fdfcf9] rounded-[24px] shadow-2xl p-8 relative flex flex-col justify-between border-[12px] border-[#f5ece1] overflow-hidden gold-glow">
        
        {/* Soft Golden Stars and Clouds Background */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.08]">
          <svg className="w-full h-full" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="15" r="1" fill="#d97706" />
            <circle cx="80" cy="25" r="1.5" fill="#d97706" />
            <circle cx="50" cy="45" r="1" fill="#d97706" />
            <circle cx="15" cy="75" r="2" fill="#d97706" />
            <circle cx="85" cy="80" r="1" fill="#d97706" />
          </svg>
        </div>

        {/* Double Inner Frame */}
        <div className="absolute inset-2 border border-[#eae0d2] rounded-[16px] pointer-events-none"></div>
        <div className="absolute inset-3 border border-[#c5a059]/20 rounded-[14px] pointer-events-none"></div>

        {/* Content Container */}
        <div className="flex-grow flex flex-col justify-center items-center text-center relative z-10 py-2 px-2">
          
          {/* Sleeping Teddy on Moon SVG */}
          <div className="w-24 h-24 mb-4 opacity-90">
            <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              {/* Crescent Moon */}
              <path d="M60,20 C35,20 20,40 20,60 C20,75 30,90 50,90 C35,90 30,75 30,60 C30,45 45,30 60,30 C65,30 70,33 75,37 C70,27 65,20 60,20 Z" fill="url(#moonGlow)" />
              {/* Teddy sleeping on moon curve */}
              <circle cx="38" cy="55" r="7" fill="#dfc096" /> {/* Head */}
              <path d="M38,62 C34,62 30,68 33,74 C36,78 44,78 47,74 C49,68 44,62 38,62 Z" fill="#cfab7e" /> {/* Body */}
              <circle cx="34" cy="52" r="2.5" fill="#dfc096" /> {/* Ear Left */}
              <circle cx="42" cy="52" r="2.5" fill="#dfc096" /> {/* Ear Right */}
              {/* Cute sleeping eyes */}
              <path d="M35,56 Q37,58 39,56" stroke="#5c4033" strokeWidth="0.8" strokeLinecap="round" />
              {/* Clouds underneath */}
              <path d="M15,80 C15,70 30,70 35,75 C40,70 55,70 60,75 C65,70 80,70 80,80 C80,85 15,85 15,80 Z" fill="#f8f4eb" opacity="0.9" />
              {/* Sparkles */}
              <path d="M72,40 L73,43 L76,44 L73,45 L72,48 L71,45 L68,44 L71,43 Z" fill="#fbbf24" />
              <path d="M28,30 L29,32 L31,32 L29,33 L30,35 L28,34 L26,35 L27,33 L25,32 L27,32 Z" fill="#fbbf24" />
              <defs>
                <linearGradient id="moonGlow" x1="20" y1="20" x2="80" y2="80">
                  <stop offset="0%" stopColor="#f59e0b" />
                  <stop offset="100%" stopColor="#fbbf24" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          <span className="font-sans-dreamy text-[9px] tracking-[0.3em] text-champagne uppercase font-bold mb-3">
            A Dream Is A Wish Your Heart Makes
          </span>

          <h2 className="font-serif-dreamy text-3xl md:text-4xl text-[#7c2d12] tracking-wider font-light mb-1">
            BABY SHOWER
          </h2>

          <span className="font-sans-dreamy text-xs text-champagne italic mb-6">
            to honor the mother-to-be
          </span>

          <h1 className="font-cursive-dreamy text-6xl text-[#b45309] leading-none mb-6 py-1">
            {motherName}
          </h1>

          <div className="w-12 h-px bg-[#c5a059]/40 mb-6"></div>

          <p className="font-sans-dreamy text-xs tracking-wider text-stone-500 mb-6 leading-relaxed">
            Hosted with love by <br />
            <span className="font-semibold text-stone-800">{hostedBy}</span>
          </p>

          {/* Date & Time block */}
          <div className="flex flex-col items-center mb-6">
            <span className="font-serif-dreamy text-xl text-[#7c2d12] font-normal">
              {dayOfWeek}, {formattedDate}
            </span>
            <span className="font-sans-dreamy text-xs tracking-widest text-[#b45309] uppercase font-bold mt-1">
              At {eventTime}
            </span>
          </div>

          {/* Venue block */}
          <div className="flex flex-col items-center mb-6 max-w-[280px]">
            <span className="font-serif-dreamy text-sm font-semibold text-stone-800 mb-1">
              {venueName}
            </span>
            <span className="font-sans-dreamy text-xs text-stone-500 leading-normal">
              {venueAddress}
            </span>
          </div>

          {/* Dress Code */}
          {dressCode && (
            <div className="mb-6 px-4 py-1.5 bg-[#fdf6e2] rounded-full border border-[#fef08a]">
              <span className="font-sans-dreamy text-[10px] tracking-wider text-[#b45309] uppercase font-bold">
                Dress Code: {dressCode}
              </span>
            </div>
          )}

          {/* Message */}
          {message && (
            <p className="font-serif-dreamy text-xs text-stone-500 italic leading-relaxed max-w-[300px] mb-6">
              "{message}"
            </p>
          )}

          {/* RSVP Button inside Card */}
          <div className="mt-2 w-full flex justify-center">
            <button
              type="button"
              onClick={() => setShowRsvpModal(true)}
              className="inline-flex items-center justify-center px-8 py-3 bg-[#7c2d12] hover:bg-[#60210a] text-white font-bold text-sm uppercase tracking-widest rounded-full shadow-md transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0"
            >
              RSVP Now
            </button>
          </div>

        </div>
      </div>

      {/* RSVP Modal Popup */}
      {showRsvpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-stone-950/65 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setShowRsvpModal(false)}
          />
          
          {/* Modal Container */}
          <div className="relative w-full max-w-md bg-stone-900 border border-white/10 rounded-3xl p-6 shadow-2xl z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setShowRsvpModal(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-6">
              <h3 className="text-2xl font-serif text-white font-semibold">Are You Attending?</h3>
              <p className="text-white/60 text-sm mt-1">Please RSVP to let us know if you're coming!</p>
            </div>
            
            <RsvpForm invitationId={invitationId} isLive={isLive} externalLink={data.rsvpLink || null} />
          </div>
        </div>
      )}
    </div>
  );
}

registry.register({ metadata: META, component: MoonStars });
